import React, { ComponentPropsWithRef, ElementType, useCallback } from "react";
import {
  motion,
  useMotionTemplate,
  useReducedMotion,
  useSpring,
  useTransform,
} from "framer-motion";

export const ShieldAnimation = <AsElement extends ElementType>({
  children,
  as,
  maxTilt = 6,
  ...props
}: {
  children?: React.ReactNode;
  as: AsElement;
  maxTilt?: number;
} & ComponentPropsWithRef<AsElement>) => {
  const shouldReduceMotion = useReducedMotion();
  const springConfig = {
    stiffness: 200,
    damping: 50,
  };
  const x = useSpring(0, springConfig);
  const y = useSpring(0, springConfig);
  const active = useSpring(0, springConfig);

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      // When the user requests reduced motion, don't calculate anything
      if (shouldReduceMotion) return;

      // Pull animated container and determine the bounding box
      const container = e.currentTarget;
      const boundingBox = container.getBoundingClientRect();

      // Calculate x and y of mouse relative to element
      // 0,0 is center
      // 1,1 is bottom right
      // -1,-1 is top left
      x.set(((e.clientX - boundingBox.left) / boundingBox.width - 0.5) * 2);
      y.set(((e.clientY - boundingBox.top) / boundingBox.height - 0.5) * 2);
      active.set(1);
    },
    [x, y, active, shouldReduceMotion]
  );

  // When the mouse leaves the element, reset to the initial state
  const onMouseLeave = useCallback(() => {
    x.set(0);
    y.set(0);
    active.set(0);
  }, [x, y, active]);

  // Convert component into an animated component
  const Component = motion(as);

  // Map values to final
  const rotateX = useTransform(y, [-1, 1], [maxTilt, -maxTilt]);
  const rotateY = useTransform(x, [-1, 1], [-maxTilt, maxTilt]);
  const translateZ = useTransform(active, [0, 1], [0, 12]);

  // This is created because the perspective must be set in the transform string, not as a CSS property (framer default)
  const transform = useMotionTemplate`perspective(200px) translateZ(${translateZ}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;

  return (
    <Component
      {...props}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
      style={{
        transform,
      }}
    >
      {children}
    </Component>
  );
};
